// camera.js - 카메라 제어

import { t } from './i18n/index.js';

let videoStream = null;
let videoElement = null;
let canvasElement = null;
let imageCapture = null;  // ImageCapture API 인스턴스

// 프리뷰 해상도 (5MP — ONNX 입력 256x256, 프리뷰로 충분)
// 캡처 시에만 ImageCapture API로 고해상도 촬영
const PREVIEW_WIDTH = 2560;
const PREVIEW_HEIGHT = 1920;

/**
 * 카메라 초기화
 * 프리뷰는 항상 5MP. 캡처 시 ImageCapture API로 고해상도 촬영 (지원 시).
 * @param {HTMLVideoElement} video - 비디오 엘리먼트
 * @param {HTMLCanvasElement} canvas - 캔버스 엘리먼트
 */
export async function initCamera(video, canvas) {
  videoElement = video;
  canvasElement = canvas;

  try {
    // 기존 스트림 정리
    stopCamera();

    const constraints = {
      video: {
        facingMode: { ideal: 'environment' },
        width: { ideal: PREVIEW_WIDTH },
        height: { ideal: PREVIEW_HEIGHT }
      },
      audio: false
    };

    videoStream = await navigator.mediaDevices.getUserMedia(constraints);
    videoElement.srcObject = videoStream;

    // ImageCapture API 초기화 (Chrome Android, Samsung Internet 지원)
    imageCapture = null;
    if (typeof ImageCapture !== 'undefined') {
      try {
        const track = videoStream.getVideoTracks()[0];
        imageCapture = new ImageCapture(track);
        console.log('ImageCapture API available');
      } catch (e) {
        console.warn('ImageCapture init failed, using canvas fallback:', e.message);
      }
    }

    // 비디오 로드 대기
    await new Promise((resolve, reject) => {
      videoElement.onloadedmetadata = () => {
        videoElement.play()
          .then(resolve)
          .catch(reject);
      };
      videoElement.onerror = reject;
    });

    return true;
  } catch (e) {
    console.error('Camera initialization failed:', e);

    if (e.name === 'NotAllowedError') {
      throw new Error(t('camera.error.notAllowed'));
    } else if (e.name === 'NotFoundError') {
      throw new Error(t('camera.error.notFound'));
    } else {
      throw new Error(t('camera.error.generic', { message: e.message }));
    }
  }
}

/**
 * 카메라 중지
 */
export function stopCamera() {
  imageCapture = null;
  if (videoStream) {
    videoStream.getTracks().forEach(track => track.stop());
    videoStream = null;
  }
  if (videoElement) {
    videoElement.srcObject = null;
  }
}

// 캡처 최대 해상도 (iOS Safari 16MP 제한 고려, 안전하게 12MP)
const MAX_CAPTURE_PIXELS = 12 * 1000 * 1000;
const MAX_CAPTURE_EDGE = 4032;

/**
 * 사진 촬영
 * @returns {string} 이미지 dataURL (JPEG)
 */
export function capturePhoto() {
  if (!videoElement || !canvasElement) {
    throw new Error(t('camera.error.notInitialized'));
  }

  const { videoWidth, videoHeight } = videoElement;

  if (!videoWidth || !videoHeight) {
    throw new Error(t('camera.error.streamNotReady'));
  }

  console.log(`Camera capture: ${videoWidth}x${videoHeight} (${(videoWidth * videoHeight / 1000000).toFixed(1)}MP)`);

  // 너무 큰 이미지는 다운샘플링 (iOS Safari 제한 대비)
  let outputWidth = videoWidth;
  let outputHeight = videoHeight;
  const totalPixels = videoWidth * videoHeight;
  const maxEdge = Math.max(videoWidth, videoHeight);

  if (totalPixels > MAX_CAPTURE_PIXELS || maxEdge > MAX_CAPTURE_EDGE) {
    const scale = Math.min(
      Math.sqrt(MAX_CAPTURE_PIXELS / totalPixels),
      MAX_CAPTURE_EDGE / maxEdge
    );
    outputWidth = Math.round(videoWidth * scale);
    outputHeight = Math.round(videoHeight * scale);
    console.log(`Downsampled to: ${outputWidth}x${outputHeight} (${(outputWidth * outputHeight / 1000000).toFixed(1)}MP)`);
  }

  // 캔버스 크기 설정
  canvasElement.width = outputWidth;
  canvasElement.height = outputHeight;

  // 비디오 프레임을 캔버스에 그리기
  const ctx = canvasElement.getContext('2d');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(videoElement, 0, 0, outputWidth, outputHeight);

  // JPEG로 변환 (품질 0.92)
  const dataURL = canvasElement.toDataURL('image/jpeg', 0.92);

  return dataURL;
}

/**
 * 고해상도 사진 촬영 (ImageCapture API 사용, 미지원 시 canvas fallback)
 * ImageCapture는 프리뷰 스트림과 무관하게 센서 최대 해상도로 스틸 촬영 가능.
 * @returns {Promise<string>} 이미지 dataURL (JPEG)
 */
export async function captureHighResPhoto() {
  // ImageCapture 미지원 (iOS Safari 등) → canvas fallback
  if (!imageCapture) {
    return capturePhoto();
  }

  try {
    const blob = await imageCapture.takePhoto({ imageWidth: 4032 });
    const dataURL = await blobToDataURL(blob);

    // 해상도 로그
    const img = await loadImageFromURL(dataURL);
    console.log(`ImageCapture: ${img.width}x${img.height} (${(img.width * img.height / 1000000).toFixed(1)}MP)`);

    return dataURL;
  } catch (e) {
    console.warn('ImageCapture.takePhoto() failed, using canvas fallback:', e.message);
    return capturePhoto();
  }
}

function blobToDataURL(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function loadImageFromURL(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}

/**
 * 카메라 활성화 여부
 */
export function isCameraActive() {
  return videoStream !== null && videoStream.active;
}

/**
 * dataURL을 Blob으로 변환
 */
export function dataURLToBlob(dataURL) {
  const parts = dataURL.split(',');
  const mime = parts[0].match(/:(.*?);/)[1];
  const bstr = atob(parts[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new Blob([u8arr], { type: mime });
}
